import React, { useState, useEffect } from 'react';
import { 
    collection, 
    query, 
    onSnapshot, 
    doc, 
    setDoc 
} from 'firebase/firestore';

const AdminTodoList = ({ user, db, setModalMessage, userColor, appId, onShowTodoModal, allAdmins = [] }) => {
    const [todos, setTodos] = useState([]);
    const [filterPriority, setFilterPriority] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Fetch todos from Firestore
    useEffect(() => {
        if (!user || !db || !appId) return;

        const todosCollectionRef = collection(db, `artifacts/${appId}/public/data/todos`);
        const q = query(todosCollectionRef);

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedTodos = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setTodos(fetchedTodos);
        }, (error) => {
            console.error("Error fetching todos:", error);
            setModalMessage(`Error loading todos: ${error.message}`);
        });

        return () => unsubscribe();
    }, [user, db, setModalMessage, appId]);

    const toggleTodoCompletion = async (todoId, currentCompletedStatus) => {
        if (!user || !db || !appId) {
            setModalMessage("User not authenticated or database not available.");
            return;
        }
        try {
            const todoDocRef = doc(db, `artifacts/${appId}/public/data/todos`, todoId);
            await setDoc(todoDocRef, { completed: !currentCompletedStatus }, { merge: true });
            setModalMessage("Todo completion status updated!");
        } catch (error) {
            console.error("Error toggling todo completion:", error);
            setModalMessage(`Failed to update todo status: ${error.message}`);
        }
    };

    const openAddTodoModal = () => {
        onShowTodoModal(null); // null means new todo
    };

    const openEditTodoModal = (todo) => {
        onShowTodoModal(todo);
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return { bg: 'from-red-500/20 to-pink-500/20', text: 'text-red-400', border: 'border-red-500/40', icon: 'fas fa-exclamation-triangle' };
            case 'medium': return { bg: 'from-yellow-500/20 to-orange-500/20', text: 'text-yellow-400', border: 'border-yellow-500/40', icon: 'fas fa-exclamation-circle' };
            case 'low': return { bg: 'from-green-500/20 to-emerald-500/20', text: 'text-green-400', border: 'border-green-500/40', icon: 'fas fa-info-circle' };
            default: return { bg: 'from-gray-500/20 to-gray-600/20', text: 'text-gray-400', border: 'border-gray-500/40', icon: 'fas fa-circle' };
        }
    };

    const getStatusColor = (completed, isOverdue) => {
        if (completed) return { bg: 'from-green-500/20 to-emerald-500/20', text: 'text-green-400', border: 'border-green-500/40', icon: 'fas fa-check-circle' };
        if (isOverdue) return { bg: 'from-red-500/20 to-pink-500/20', text: 'text-red-400', border: 'border-red-500/40', icon: 'fas fa-clock' };
        return { bg: 'from-blue-500/20 to-indigo-500/20', text: 'text-blue-400', border: 'border-blue-500/40', icon: 'fas fa-play-circle' };
    };

    const calculateTaskProgress = (todos) => {
        if (!todos || todos.length === 0) return { completed: 0, total: 0, percentage: 0 };
        
        let completed = 0;
        let total = 0;
        
        todos.forEach(todoItem => {
            total++;
            if (todoItem.completed) completed++;
            
            if (todoItem.subtasks) {
                todoItem.subtasks.forEach(subtask => {
                    total++;
                    if (subtask.completed) completed++;
                    
                    if (subtask.subSubtasks) {
                        subtask.subSubtasks.forEach(subSubtask => {
                            total++;
                            if (subSubtask.completed) completed++;
                        });
                    }
                });
            }
        });
        
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
        return { completed, total, percentage };
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No due date';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    const formatAssignedAdmins = (assignedTo, allAdmins = []) => {
        if (!assignedTo || assignedTo.length === 0) return null;
        
        return assignedTo.map(uid => {
            // Try to find the admin in the allAdmins array if provided
            const admin = allAdmins.find(a => a.uid === uid);
            return {
                uid: uid,
                username: admin?.username || admin?.email || `User ${uid.substring(0, 8)}`,
                color: admin?.color || '#4d9eff'
            };
        });
    };

    // Filter todos based on search and filters
    const filteredTodos = todos.filter(todo => {
        const matchesSearch = todo.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            (todo.description && todo.description.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesPriority = filterPriority === 'all' || todo.priority === filterPriority;
        const isOverdue = todo.dueDate && new Date(todo.dueDate) < new Date();
        const matchesStatus = filterStatus === 'all' || 
                            (filterStatus === 'completed' && todo.completed) ||
                            (filterStatus === 'pending' && !todo.completed && !isOverdue) ||
                            (filterStatus === 'overdue' && isOverdue && !todo.completed);
        
        return matchesSearch && matchesPriority && matchesStatus;
    });

    // Calculate overall statistics
    const totalTasks = todos.length;
    const completedTasks = todos.filter(todo => todo.completed).length;
    const overdueTasks = todos.filter(todo => todo.dueDate && new Date(todo.dueDate) < new Date() && !todo.completed).length;
    const highPriorityTasks = todos.filter(todo => todo.priority === 'high' && !todo.completed).length;

    return (
        <div className="space-y-8 animate-fadeIn">
            {/* Header Section with Glass Morphism */}
            <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-indigo-500/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500 animate-pulse-slow"></div>
                <div className="relative glass-card border border-blue-500/30 rounded-2xl p-8 hover:border-blue-400/50 transition-all duration-500 hover-lift">
                    {/* Floating decorative elements */}
                    <div className="absolute top-6 right-6 w-4 h-4 bg-blue-500 rounded-full animate-ping opacity-20"></div>
                    <div className="absolute bottom-6 left-6 w-3 h-3 bg-purple-400 rounded-full animate-pulse opacity-30"></div>
                    
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center space-x-4">
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-indigo-500/20 rounded-2xl flex items-center justify-center animate-float">
                                    <i className="fas fa-tasks text-blue-400 text-2xl"></i>
                                </div>
                                <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center animate-bounce">
                                    <i className="fas fa-bolt text-white text-xs"></i>
                                </div>
                            </div>
                            <div>
                                <h2 className="text-3xl font-bold bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent animate-slideInLeft">
                                    Task Management Center
                                </h2>
                                <p className="text-gray-400 animate-slideInLeft" style={{ animationDelay: '0.1s' }}>
                                    Organize, track, and collaborate on team objectives
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={openAddTodoModal}
                            className="group relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700 text-white font-bold py-4 px-8 rounded-2xl transition-all duration-500 shadow-2xl hover:shadow-blue-500/50 hover:shadow-3xl hover:scale-105 animate-bounceIn"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                            <div className="relative flex items-center space-x-3">
                                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center group-hover:rotate-90 transition-transform duration-300">
                                    <i className="fas fa-plus text-lg"></i>
                                </div>
                                <span className="text-lg">Create New Task</span>
                                <i className="fas fa-rocket group-hover:translate-x-1 transition-transform duration-300"></i>
                            </div>
                        </button>
                    </div>

                    {/* Statistics Dashboard */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {[
                            {
                                title: 'Total Tasks',
                                value: totalTasks,
                                icon: 'fas fa-list-check',
                                color: 'from-blue-500/20 to-indigo-500/20',
                                iconColor: 'text-blue-400',
                                borderColor: 'border-blue-500/40',
                                delay: '0s'
                            },
                            {
                                title: 'Completed',
                                value: completedTasks,
                                icon: 'fas fa-check-circle',
                                color: 'from-green-500/20 to-emerald-500/20',
                                iconColor: 'text-green-400',
                                borderColor: 'border-green-500/40',
                                delay: '0.1s'
                            },
                            {
                                title: 'Overdue',
                                value: overdueTasks,
                                icon: 'fas fa-exclamation-triangle',
                                color: 'from-red-500/20 to-pink-500/20',
                                iconColor: 'text-red-400',
                                borderColor: 'border-red-500/40',
                                delay: '0.2s'
                            },
                            {
                                title: 'High Priority',
                                value: highPriorityTasks,
                                icon: 'fas fa-fire',
                                color: 'from-orange-500/20 to-red-500/20',
                                iconColor: 'text-orange-400',
                                borderColor: 'border-orange-500/40',
                                delay: '0.3s'
                            }
                        ].map((stat, index) => (
                            <div key={index} className="group relative animate-slideInLeft" style={{ animationDelay: stat.delay }}>
                                <div className={`absolute inset-0 bg-gradient-to-r ${stat.color} rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                                <div className={`relative glass-card border ${stat.borderColor} rounded-xl p-6 h-24 transition-all duration-300 hover-lift flex items-center`}>
                                    <div className="flex items-center justify-between w-full">
                                        <div className="flex-1">
                                            <p className="text-gray-400 text-sm font-medium">{stat.title}</p>
                                            <p className={`text-3xl font-bold ${stat.iconColor} mt-1`}>{stat.value}</p>
                                        </div>
                                        <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                                            <i className={`${stat.icon} ${stat.iconColor} text-xl`}></i>
                                        </div>
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Advanced Filters Section */}
            <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500 animate-pulse-slow"></div>
                <div className="relative glass-card border border-purple-500/30 rounded-2xl p-6 hover:border-purple-400/50 transition-all duration-500">
                    <div className="flex items-center space-x-4 mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl flex items-center justify-center animate-pulse">
                            <i className="fas fa-filter text-purple-400 text-xl"></i>
                        </div>
                        <h3 className="text-xl font-bold gradient-text">Smart Filters & Search</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Search Input */}
                        <div className="relative group/search">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl opacity-0 group-hover/search:opacity-100 transition-opacity duration-300"></div>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <i className="fas fa-search text-gray-400 group-hover/search:text-blue-400 transition-colors duration-300"></i>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search tasks, descriptions..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400/50 transition-all duration-300"
                                />
                            </div>
                        </div>

                        {/* Priority Filter */}
                        <div className="relative group/priority">
                            <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl opacity-0 group-hover/priority:opacity-100 transition-opacity duration-300"></div>
                            <select
                                value={filterPriority}
                                onChange={(e) => setFilterPriority(e.target.value)}
                                className="relative w-full py-3 px-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-400/50 transition-all duration-300"
                            >
                                <option value="all">All Priorities</option>
                                <option value="high">🔥 High Priority</option>
                                <option value="medium">⚡ Medium Priority</option>
                                <option value="low">💚 Low Priority</option>
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div className="relative group/status">
                            <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl opacity-0 group-hover/status:opacity-100 transition-opacity duration-300"></div>
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="relative w-full py-3 px-4 bg-gray-900/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-400/50 transition-all duration-300"
                            >
                                <option value="all">All Status</option>
                                <option value="pending">🟡 In Progress</option>
                                <option value="completed">✅ Completed</option>
                                <option value="overdue">🚨 Overdue</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tasks List */}
            <div className="space-y-6">
                {filteredTodos.length === 0 ? (
                    <div className="relative group">
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-500/5 via-blue-500/5 to-purple-500/5 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                        <div className="relative glass-card border border-gray-500/20 rounded-2xl p-12 text-center hover:border-gray-400/30 transition-all duration-500">
                            <div className="w-24 h-24 bg-gradient-to-br from-gray-500/10 to-blue-500/10 rounded-3xl flex items-center justify-center mx-auto mb-6 animate-float">
                                <i className="fas fa-clipboard-list text-gray-400 text-4xl"></i>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-3">No Tasks Found</h3>
                            <p className="text-gray-400 text-lg mb-6">
                                {todos.length === 0 
                                    ? "Ready to boost productivity? Create your first task to get started!" 
                                    : "No tasks match your current filters. Try adjusting your search criteria."
                                }
                            </p>
                            {todos.length === 0 && (
                                <button
                                    onClick={openAddTodoModal}
                                    className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                                    <div className="relative flex items-center space-x-2">
                                        <i className="fas fa-plus group-hover:rotate-90 transition-transform duration-300"></i>
                                        <span>Create Your First Task</span>
                                    </div>
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    filteredTodos.map((todo, index) => {
                        const progress = calculateTaskProgress(todo.todos || []);
                        const isOverdue = todo.dueDate && new Date(todo.dueDate) < new Date() && !todo.completed;
                        const priorityStyle = getPriorityColor(todo.priority);
                        const statusStyle = getStatusColor(todo.completed, isOverdue);
                        
                        return (
                            <div key={todo.id} className="group/task relative animate-slideInLeft" style={{ animationDelay: `${index * 0.05}s` }}>
                                <div className={`absolute inset-0 bg-gradient-to-r ${statusStyle.bg} rounded-2xl blur-lg group-hover/task:blur-xl transition-all duration-500 opacity-0 group-hover/task:opacity-100`}></div>
                                <div className={`relative glass-card border ${statusStyle.border} rounded-2xl p-8 transition-all duration-500 hover:border-opacity-80 hover-lift overflow-hidden`}>
                                    {/* Floating decoration elements */}
                                    <div className="absolute top-4 right-4 w-3 h-3 bg-blue-400 rounded-full animate-ping opacity-20"></div>
                                    <div className="absolute bottom-4 left-4 w-2 h-2 bg-purple-400 rounded-full animate-pulse opacity-30" style={{ animationDelay: '0.5s' }}></div>
                                    
                                    {/* Task Header */}
                                    <div className="flex items-start justify-between mb-6">
                                        <div className="flex items-start space-x-4 flex-1 min-w-0">
                                            {/* Custom Checkbox */}
                                            <div className="relative mt-1">
                                                <input
                                                    type="checkbox"
                                                    checked={todo.completed || false}
                                                    onChange={() => toggleTodoCompletion(todo.id, todo.completed)}
                                                    className="sr-only peer"
                                                />
                                                <div className={`w-6 h-6 rounded-full border-2 ${statusStyle.border} bg-gradient-to-br ${statusStyle.bg} flex items-center justify-center cursor-pointer transition-all duration-300 peer-checked:scale-110 peer-checked:bg-gradient-to-br peer-checked:from-green-500/30 peer-checked:to-emerald-500/30 peer-checked:border-green-400 hover:scale-105`}>
                                                    {todo.completed && (
                                                        <i className="fas fa-check text-green-400 text-sm animate-bounceIn"></i>
                                                    )}
                                                </div>
                                                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                                            </div>
                                            
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <h3 className={`text-xl font-bold transition-all duration-300 ${todo.completed ? 'line-through text-gray-500' : 'text-white'}`}>
                                                        {todo.title}
                                                    </h3>
                                                    
                                                    {/* Priority Badge */}
                                                    <div className={`relative overflow-hidden bg-gradient-to-r ${priorityStyle.bg} border ${priorityStyle.border} rounded-full px-3 py-1 group/priority`}>
                                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover/priority:translate-x-[100%] transition-transform duration-500"></div>
                                                        <div className="relative flex items-center space-x-1">
                                                            <i className={`${priorityStyle.icon} ${priorityStyle.text} text-xs`}></i>
                                                            <span className={`${priorityStyle.text} text-xs font-bold uppercase tracking-wider`}>
                                                                {todo.priority || 'LOW'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Status Badge */}
                                                    <div className={`relative overflow-hidden bg-gradient-to-r ${statusStyle.bg} border ${statusStyle.border} rounded-full px-3 py-1 group/status`}>
                                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover/status:translate-x-[100%] transition-transform duration-500"></div>
                                                        <div className="relative flex items-center space-x-1">
                                                            <i className={`${statusStyle.icon} ${statusStyle.text} text-xs`}></i>
                                                            <span className={`${statusStyle.text} text-xs font-bold uppercase tracking-wider`}>
                                                                {todo.completed ? 'Completed' : (isOverdue ? 'Overdue' : 'In Progress')}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {todo.description && (
                                                    <p className={`text-gray-300 mb-4 leading-relaxed ${todo.completed ? 'opacity-60' : ''}`}>
                                                        {todo.description}
                                                    </p>
                                                )}
                                                
                                                {/* Task Meta Information */}
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                    <div className="flex items-center space-x-2">
                                                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-lg flex items-center justify-center">
                                                            <i className="fas fa-calendar text-blue-400 text-sm"></i>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-gray-400 uppercase tracking-wider">Due Date</p>
                                                            <p className={`text-sm font-medium ${isOverdue ? 'text-red-400' : 'text-white'}`}>
                                                                {formatDate(todo.dueDate)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    
                                    <div className="flex items-center space-x-2">
                                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
                                            <i className="fas fa-users text-purple-400 text-sm"></i>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-gray-400 uppercase tracking-wider">Assigned To</p>
                                            <div className="flex items-center space-x-2 mt-1">
                                                {(() => {
                                                    const assignedUsers = formatAssignedAdmins(todo.assignedTo, allAdmins);
                                                    if (!assignedUsers || assignedUsers.length === 0) {
                                                        return (
                                                            <div className="flex items-center space-x-2">
                                                                <div className="w-6 h-6 bg-gray-600/50 rounded-full flex items-center justify-center">
                                                                    <i className="fas fa-user-slash text-gray-500 text-xs"></i>
                                                                </div>
                                                                <span className="text-sm text-gray-500">Unassigned</span>
                                                            </div>
                                                        );
                                                    }
                                                    
                                                    return (
                                                        <div className="flex items-center space-x-1">
                                                            {assignedUsers.slice(0, 3).map((assignedUser, index) => (
                                                                <div
                                                                    key={assignedUser.uid}
                                                                    className="relative group/avatar"
                                                                    title={assignedUser.username}
                                                                >
                                                                    <div
                                                                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg transition-all duration-300 group-hover/avatar:scale-110 group-hover/avatar:z-10 border border-white/20"
                                                                        style={{ 
                                                                            backgroundColor: assignedUser.color,
                                                                            marginLeft: index > 0 ? '-4px' : '0'
                                                                        }}
                                                                    >
                                                                        {assignedUser.username.charAt(0).toUpperCase()}
                                                                    </div>
                                                                    
                                                                    {/* Hover tooltip */}
                                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover/avatar:opacity-100 transition-opacity duration-200 pointer-events-none z-20">
                                                                        <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
                                                                            {assignedUser.username}
                                                                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                            
                                                            {assignedUsers.length > 3 && (
                                                                <div 
                                                                    className="w-6 h-6 bg-gray-600/80 rounded-full flex items-center justify-center text-gray-300 text-xs font-bold shadow-lg border border-white/20"
                                                                    style={{ marginLeft: '-4px' }}
                                                                    title={`+${assignedUsers.length - 3} more`}
                                                                >
                                                                    +{assignedUsers.length - 3}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>                                                    {progress.total > 0 && (
                                                        <div className="flex items-center space-x-2">
                                                            <div className="w-8 h-8 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg flex items-center justify-center">
                                                                <i className="fas fa-chart-pie text-green-400 text-sm"></i>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-gray-400 uppercase tracking-wider">Progress</p>
                                                                <p className="text-sm font-medium text-white">
                                                                    {progress.completed}/{progress.total} ({progress.percentage}%)
                                                                </p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Enhanced Progress Bar */}
                                                {progress.total > 0 && (
                                                    <div className="relative mb-6">
                                                        <div className="flex items-center justify-between text-sm mb-2">
                                                            <span className="text-gray-400">Task Progress</span>
                                                            <span className="text-white font-medium">{progress.percentage}%</span>
                                                        </div>
                                                        <div className="relative w-full h-3 bg-gray-800 rounded-full overflow-hidden">
                                                            <div className="absolute inset-0 bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700 animate-pulse"></div>
                                                            <div 
                                                                className="relative h-full bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 rounded-full transition-all duration-1000 ease-out shadow-lg"
                                                                style={{ width: `${progress.percentage}%` }}
                                                            >
                                                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                                                            </div>
                                                            <div className="absolute inset-0 rounded-full shadow-inner"></div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex items-center space-x-3 ml-6">
                                            <button
                                                onClick={() => openEditTodoModal(todo)}
                                                className="group relative overflow-hidden bg-gradient-to-r from-blue-600/20 to-purple-600/20 hover:from-blue-600/40 hover:to-purple-600/40 border border-blue-500/30 hover:border-blue-400/50 text-blue-400 hover:text-blue-300 p-3 rounded-xl transition-all duration-300 hover:scale-105"
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500"></div>
                                                <i className="fas fa-edit relative text-lg group-hover:rotate-12 transition-transform duration-300"></i>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Subtasks Display - Enhanced */}
                                    {todo.todos && todo.todos.length > 0 && (
                                        <div className="relative mt-6 pt-6 border-t border-gray-700/50">
                                            <div className="flex items-center space-x-3 mb-4">
                                                <div className="w-8 h-8 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-lg flex items-center justify-center">
                                                    <i className="fas fa-list text-indigo-400 text-sm"></i>
                                                </div>
                                                <h4 className="font-bold text-white">Task Breakdown</h4>
                                                <div className="flex-1 h-px bg-gradient-to-r from-indigo-500/50 to-transparent"></div>
                                            </div>
                                            
                                            <div className="space-y-3 ml-4">
                                                {todo.todos.map((todoItem, itemIndex) => (
                                                    <div key={todoItem.id || itemIndex} className="group/item relative">
                                                        {/* Main Task Item */}
                                                        <div className={`flex items-center space-x-3 p-3 rounded-lg bg-gradient-to-r from-gray-800/50 to-gray-700/30 border border-gray-600/30 transition-all duration-300 hover:border-gray-500/50 ${todoItem.completed ? 'opacity-60' : ''}`}>
                                                            <div className="relative">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={todoItem.completed || false}
                                                                    readOnly
                                                                    className="sr-only"
                                                                />
                                                                <div className={`w-4 h-4 rounded-full border-2 ${todoItem.completed ? 'border-green-400 bg-green-500/20' : 'border-gray-500 bg-gray-700/50'} flex items-center justify-center`}>
                                                                    {todoItem.completed && (
                                                                        <i className="fas fa-check text-green-400 text-xs"></i>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <span className={`flex-1 text-sm ${todoItem.completed ? 'line-through text-gray-500' : 'text-gray-200'}`}>
                                                                {todoItem.text}
                                                            </span>
                                                            {todoItem.subtasks && todoItem.subtasks.length > 0 && (
                                                                <div className="text-xs text-gray-400 bg-gray-600/30 px-2 py-1 rounded-full">
                                                                    {todoItem.subtasks.filter(sub => sub.completed).length}/{todoItem.subtasks.length} subtasks
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Subtasks */}
                                                        {todoItem.subtasks && todoItem.subtasks.map((subtask, subIndex) => (
                                                            <div key={subtask.id || subIndex} className="ml-8 mt-2">
                                                                <div className={`flex items-center space-x-3 p-2 rounded-lg bg-gradient-to-r from-gray-700/30 to-gray-600/20 border border-gray-600/20 transition-all duration-300 ${subtask.completed ? 'opacity-50' : ''}`}>
                                                                    <div className="relative">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={subtask.completed || false}
                                                                            readOnly
                                                                            className="sr-only"
                                                                        />
                                                                        <div className={`w-3 h-3 rounded-full border ${subtask.completed ? 'border-green-400 bg-green-500/20' : 'border-gray-500 bg-gray-700/50'} flex items-center justify-center`}>
                                                                            {subtask.completed && (
                                                                                <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <span className={`flex-1 text-xs ${subtask.completed ? 'line-through text-gray-500' : 'text-gray-300'}`}>
                                                                        {subtask.text}
                                                                    </span>
                                                                    {subtask.subSubtasks && subtask.subSubtasks.length > 0 && (
                                                                        <div className="text-xs text-gray-500 bg-gray-600/20 px-1 py-0.5 rounded-full text-xs">
                                                                            {subtask.subSubtasks.filter(sub => sub.completed).length}/{subtask.subSubtasks.length}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Sub-subtasks */}
                                                                {subtask.subSubtasks && subtask.subSubtasks.map((subSubtask, subSubIndex) => (
                                                                    <div key={subSubtask.id || subSubIndex} className="ml-6 mt-1">
                                                                        <div className={`flex items-center space-x-2 p-1.5 rounded bg-gray-600/20 transition-all duration-300 ${subSubtask.completed ? 'opacity-40' : ''}`}>
                                                                            <div className={`w-2 h-2 rounded-full ${subSubtask.completed ? 'bg-green-400' : 'bg-gray-500'}`}></div>
                                                                            <span className={`flex-1 text-xs ${subSubtask.completed ? 'line-through text-gray-500' : 'text-gray-400'}`}>
                                                                                {subSubtask.text}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Shimmer effect overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-100%] group-hover/task:translate-x-[100%] transition-transform duration-1000 pointer-events-none"></div>
                                </div>
                            </div>
                        )
                    })
                )}
            </div>
        </div>
    );
};

export default AdminTodoList;
